/* eslint-disable @typescript-eslint/no-explicit-any */
import { ENDPOINTS } from '@/util'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useGetFilterSelections } from '@/catalog-apps/hooks/useGetFilterSelections'
import { BankReconResponse } from './types'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useGetPageFilters } from '../../AppPage2/Filter/useGetPageFilters'
import { useIsPageBuilder } from '@/catalog-apps/hooks/useIsPageBuilder'
import { TComponentType } from '../../AppPage2/types'

// Set to true to use mock data, false to use actual data
const USE_MOCK_DATA = false

interface GetBankReconParams {
  appKey?: string
  contentId?: string
  projectId?: number
  filterSelections: Record<string, any>[]
  isPageBuilder?: boolean
}

const getBankRecon = async ({ appKey, contentId, projectId, filterSelections, isPageBuilder }: GetBankReconParams) => {
  if (!appKey || !contentId || !projectId) {
    throw new Error('Missing required parameters')
  }
  if (isPageBuilder) {
    return Promise.resolve({
      ...(await import('@/catalog-apps/mock-data/bank-recon/bank-recon-response.json')).json,
    } as BankReconResponse)
  }
  if (USE_MOCK_DATA) {
    return Promise.resolve(
      (await import('@/catalog-apps/mock-data/bank-recon/bank-recon-sygnia-inflow-response.json'))
        .json as BankReconResponse
    )
  }

  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }

  const url = `${ENDPOINTS.CC_DOTNET}/content`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: contentId,
      project_id: projectId,
      filters: filterSelections,
    }),
  })

  const data = (await response.json()) as BankReconResponse
  if (response.ok) {
    return data
  }
  throw new Error('Failed to fetch bankRecon data')
}

export function useGetBankRecon(contentId: string, tileFilterSelections: Record<string, any>[]) {
  const isPageBuilder = useIsPageBuilder()
  const {
    data: { defaultSelections },
  } = useGetPageFilters()
  const { filterSelections } = useGetFilterSelections()
  const { appKey, projectId } = useAppContext()
  const filterSelectionsArray =
    filterSelections.length > 0
      ? [...filterSelections, ...tileFilterSelections]
      : [...defaultSelections, ...tileFilterSelections]

  const componentType: TComponentType = 'bank-recon'
  return useSuspenseQuery({
    queryKey: [contentId, componentType, appKey, projectId, filterSelectionsArray],
    queryFn: () =>
      getBankRecon({
        appKey,
        contentId,
        projectId,
        filterSelections: filterSelectionsArray,
        isPageBuilder,
      }),
  })
}
